import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import {
  ClassifyImageParams,
  ClassificationResult,
  ImageClassificationError,
  ImageClassificationRequest,
  ImageClassificationResponse,
  RelayEnvironment
} from '@jump-tech-frontend/domain';
import { ActivatedRoute, Params } from '@angular/router';
import { EnvironmentToken } from '@jump-tech-frontend/app-config';

@Injectable({ providedIn: 'root' })
export class ImageClassificationService {
  private readonly API_URL = this.environment.apiImageAnalysisUrl;
  private readonly DEFAULT_TIMEOUT = 30000;
  private suid: string;

  constructor(
    private http: HttpClient,
    private activatedRoute: ActivatedRoute,
    @Inject(EnvironmentToken) private environment: RelayEnvironment
  ) {
    this.subscribeToQueryParams();
  }

  /**
   * Classify an image to determine its equipment type.
   * @param params Classification parameters
   * @returns Observable of classification result
   */
  classifyImage(params: ClassifyImageParams): Observable<ClassificationResult> {
    const { base64Image, deviceContext = 'mobile', options, classificationType } = params;

    // Remove data URL prefix if present
    const cleanBase64 = this.cleanBase64(base64Image);

    // Check base64 size before API call (8MB limit)
    const MAX_BASE64_SIZE_BYTES = 8 * 1024 * 1024;
    if (cleanBase64.length > MAX_BASE64_SIZE_BYTES) {
      return of({
        status: 'error',
        success: false,
        equipmentType: 'Unknown',
        confidence: 0,
        fuseCutoutConfidence: 0,
        consumerUnitConfidence: 0,
        isolatorSwitchConfidence: 0,
        confidenceThresholdMet: false,
        qualityScore: 0,
        qualityAcceptable: false,
        qualityIssues: ['Image exceeds the maximum allowed size of 8MB.'],
        warnings: [],
        error: 'Image is too large to process. Please use a smaller image.',
        errorDetails: ['Image exceeds the maximum allowed size of 8MB.'],
        boundingBoxes: undefined,
        detectionSettings: undefined
      });
    }

    // Strict parameters for high-quality image uploads only
    const request: ImageClassificationRequest = {
      image: cleanBase64
    };

    if (classificationType) {
      request.classificationType = classificationType;
    }

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      skip: 'true'
    });

    if (deviceContext === 'mobile') {
      headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `suid ${this.suid}`
      });
    }

    return this.http.post<ImageClassificationResponse>(this.API_URL, request, { headers }).pipe(
      timeout(options?.timeout || this.DEFAULT_TIMEOUT),
      map(response => this.mapResponseToResult(response)),
      catchError(error => this.handleError(error))
    );
  }

  /**
   * Map API response to a simplified classification result
   */
  private mapResponseToResult(response: ImageClassificationResponse): ClassificationResult {
    // Handle quality_too_low status specifically
    if (response.status === 'quality_too_low') {
      const qualityDetails = response.quality_details;
      const qualityIssues = qualityDetails?.issues || [];

      // Add detailed quality scores to issues for better user feedback
      const detailedIssues = [...qualityIssues];

      return {
        status: response.status,
        success: false,
        equipmentType: 'Unknown',
        confidence: 0,
        fuseCutoutConfidence: 0,
        consumerUnitConfidence: 0,
        isolatorSwitchConfidence: 0,
        confidenceThresholdMet: false,
        qualityScore: response.quality_score || 0,
        qualityAcceptable: false,
        qualityIssues: detailedIssues,
        qualityDetails: qualityDetails,
        warnings: response.warnings,
        error: 'Image quality too low for classification',
        errorDetails: detailedIssues,
        boundingBoxes: undefined,
        detectionSettings: undefined
      };
    }

    if (response.status === 'error') {
      throw new Error(response.error || 'Classification failed');
    }

    const classification = response.classification;
    const qualityDetails = response.quality_details;

    return {
      status: response.status,
      success: response.status === 'success' || classification?.confidence_threshold_met || false,
      equipmentType: classification?.primary_classification || 'Unknown',
      confidence: classification?.primary_confidence || 0,
      fuseCutoutConfidence: classification?.fuse_cutout_confidence || 0,
      consumerUnitConfidence: classification?.consumer_unit_confidence || 0,
      isolatorSwitchConfidence: classification?.isolator_switch_confidence || 0,
      classConfidences: classification?.class_confidences ?? null,
      confidenceThresholdMet: classification?.confidence_threshold_met || false,
      qualityScore: response.quality_score || 0,
      qualityAcceptable: qualityDetails?.is_acceptable || false,
      qualityIssues: qualityDetails?.issues || [],
      qualityDetails: qualityDetails,
      warnings: response.warnings,
      boundingBoxes: this.transformBoundingBoxes(classification?.bounding_boxes),
      detectionSettings: classification?.detection_settings as any,
      imageWidth: response.image_metadata?.width,
      imageHeight: response.image_metadata?.height,
      sourceWidth: Array.isArray(response.image_metadata?.original_size)
        ? response.image_metadata?.original_size[0]
        : undefined,
      sourceHeight: Array.isArray(response.image_metadata?.original_size)
        ? response.image_metadata?.original_size[1]
        : undefined
    };
  }

  /**
   * Transform API bounding box format to UI format
   * API format: { bbox: [x1, y1, x2, y2], label: string, confidence: number (percentage) }
   * UI format: { label: string, x1: number, y1: number, x2: number, y2: number, confidence: number (percentage) }
   */
  private transformBoundingBoxes(
    detections?: Array<{ bbox: [number, number, number, number]; label: string; confidence: number }>
  ): Array<{ label: string; x1: number; y1: number; x2: number; y2: number; confidence: number }> | undefined {
    if (!detections || detections.length === 0) {
      return undefined;
    }

    return detections.map(detection => ({
      label: detection.label,
      x1: detection.bbox[0],
      y1: detection.bbox[1],
      x2: detection.bbox[2],
      y2: detection.bbox[3],
      confidence: detection.confidence // Keep as percentage for UI display
    }));
  }

  /**
   * Handle errors from the classification API
   */
  private handleError(error: {
    name?: string;
    status?: number;
    error?: ImageClassificationError;
  }): Observable<ClassificationResult> {
    console.error('Image classification error:', error);

    let errorMessage = 'Failed to classify image';
    const errorDetails: string[] = [];

    if (error.name === 'TimeoutError') {
      errorMessage = 'Classification request timed out';
      errorDetails.push('The image processing took too long. Try with a smaller image.');
    } else if (error.status === 0) {
      errorMessage = 'Unable to connect to classification service';
      errorDetails.push('Please check your network connection.');
    } else if (error.status === 400) {
      errorMessage = 'Invalid image or processing error';
      if (typeof error.error?.detail === 'string' && error.error.detail.includes('too_small')) {
        errorMessage = 'Invalid image (image too small)';
      }
      if (error.error?.error) {
        errorDetails.push(error.error.error);
      }
    } else if (error.status && error.status >= 500) {
      errorMessage = 'Classification service error';
      errorDetails.push('The service is temporarily unavailable.');
    } else if (error.error?.error) {
      errorMessage = error.error.error;
    }

    return of({
      status: 'error',
      success: false,
      equipmentType: 'Unknown',
      confidence: 0,
      fuseCutoutConfidence: 0,
      consumerUnitConfidence: 0,
      isolatorSwitchConfidence: 0,
      confidenceThresholdMet: false,
      qualityScore: 0,
      qualityAcceptable: false,
      qualityIssues: errorDetails,
      warnings: [],
      error: errorMessage,
      errorDetails,
      boundingBoxes: undefined,
      detectionSettings: undefined
    });
  }

  /**
   * Remove data URL prefix from base64 string
   */
  private cleanBase64(base64: string): string {
    if (base64.includes(',')) {
      return base64.split(',')[1];
    }
    return base64;
  }

  private subscribeToQueryParams(): void {
    this.activatedRoute.queryParams.subscribe(async (params: Params) => {
      this.suid = params['suid'] || '';
    });
  }
}
